/**
 * BuildYourOffer — Round 218A P0 FINAL (2026-02-07).
 *
 * Shown on the Build Offer result screen.
 *
 * PayGBot decides what comes next. This screen calls the canonical
 * next-action resolver (/api/foundation/next-action) and renders
 * whatever CTA / route it names. No hardcoded "Create Sales Page"
 * copy — when Brand Brain is missing the CTA says "Continue to
 * Brand Builder"; when foundation is complete it says "Build your
 * Sales Page". One source of truth.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Lightning, ListNumbers, ArrowRight } from '@phosphor-icons/react';
import useNextAction from '@/hooks/useNextAction';


const BuildYourOffer = ({
  // eslint-disable-next-line no-unused-vars
  offerType = 'digital_product',
  // eslint-disable-next-line no-unused-vars
  offerName = 'Your Offer',
  // eslint-disable-next-line no-unused-vars
  onOpenPlatform,   // kept for backward-compat — intentionally unused
  // eslint-disable-next-line no-unused-vars
  onCreateSalesPage, // deprecated — the resolver now decides routing
  onBackToDashboard,
}) => {
  const navigate = useNavigate();
  const nav = useNextAction(true);

  const primaryCta = nav.loading ? 'Continuing…' : (nav.nextCtaLabel || 'Continue');
  const primaryOnClick = () => {
    if (nav.nextRoute) {
      navigate(nav.nextRoute);
    }
  };

  return (
    <div className="space-y-4" data-testid="build-your-offer-section">
      {/* Section Header */}
      <div className="flex items-center space-x-2">
        <Lightning size={20} className="text-yellow-400" weight="fill" />
        <h3 className="text-white font-semibold text-lg">Your offer is ready</h3>
      </div>

      <div className="bg-gradient-to-br from-purple-500/15 to-pink-500/15 border border-purple-400/30 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <CheckCircle size={22} className="text-emerald-400 flex-shrink-0 mt-0.5" weight="fill" />
          <div>
            <p className="text-white font-medium">Offer saved to Business Brain.</p>
            <p className="text-slate-300 text-sm mt-1" data-testid="offer-next-reason">
              {nav.reason || 'Continuing to the next step…'}
            </p>
          </div>
        </div>
      </div>

      {/* Primary CTA — driven ENTIRELY by the canonical resolver. */}
      <button
        onClick={primaryOnClick}
        disabled={nav.loading || !nav.nextRoute}
        data-testid="offer-primary-next-btn"
        className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all shadow-lg disabled:opacity-70"
      >
        <span>{primaryCta}</span>
        <ArrowRight size={18} weight="bold" />
      </button>

      {/* Secondary — return to Dashboard. */}
      <button
        onClick={onBackToDashboard}
        data-testid="back-to-dashboard-btn"
        className="w-full py-3 bg-white/10 hover:bg-white/20 text-slate-200 font-medium rounded-xl transition-colors text-sm"
      >
        Back to Dashboard
      </button>

      {/* Resolver-driven Next Step indicator. */}
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-lg p-3 flex items-center space-x-3">
        <ListNumbers size={20} className="text-amber-400" />
        <div>
          <span className="text-xs font-medium text-amber-400 uppercase tracking-wider">Next Step</span>
          <p className="text-white text-sm font-medium" data-testid="next-step-label">
            {primaryCta}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BuildYourOffer;
